// src/javascript/components/map-track/map-track-animation.ts
const SUPPORTS_SCROLL_TIMELINE = CSS.supports('animation-timeline', 'view()');
/** Piecewise-lerp keyframes for the SVG stroke draw (dashoffset 1 → 0). */
const SVG_DASHOFFSET_KEYFRAMES = [
    [0.0, 1.0],
    [0.05, 1.0],
    [0.15, 0.9],
    [0.5, 0.55],
    [0.75, 0.25],
    [1.0, 0.0],
];
/** Piecewise-lerp keyframes for the SVG stroke opacity (0 → 1). */
const SVG_OPACITY_KEYFRAMES = [
    [0.0, 0.0],
    [0.05, 0.0],
    [0.15, 1.0],
    [1.0, 1.0],
];
/**
 * Parse a range string like "entry 40" into a [keyword, percent] tuple.
 */
function parseRange(value) {
    const parts = value.trim().split(/\s+/);
    return [parts[0], Number(parts[1])];
}
function clamp01(v) {
    return Math.min(1, Math.max(0, v));
}
function lerp(a, b, t) {
    return a + (b - a) * t;
}
function piecewiseLerp(points, t) {
    // points are [[t0,val0],[t1,val1],...] with t in [0..1]
    if (points.length === 0)
        return 0;
    if (t <= points[0][0])
        return points[0][1];
    for (let i = 0; i < points.length - 1; i++) {
        const [t0, v0] = points[i];
        const [t1, v1] = points[i + 1];
        if (t >= t0 && t <= t1) {
            const local = (t - t0) / (t1 - t0 || 1);
            return lerp(v0, v1, local);
        }
    }
    return points[points.length - 1][1];
}
function timelineProgressFor(rect, viewportH, keyword, percent) {
    const total = rect.height + viewportH;
    // Map our overall progress (0..1) to 3 phase boundaries:
    // - entry phase: element top from viewport bottom → viewport top
    // - cover phase: element fully covers viewport (if height >= viewportH, otherwise it’s a short phase)
    // - exit phase: element bottom from viewport bottom → viewport top
    //
    // This assumes overall is computed as:
    // overall = (viewportH - rect.top) / (rect.height + viewportH)
    if (total <= 0)
        return 0;
    const coverStart = viewportH / total; // when rect.top === 0  (top reaches viewport top)
    const rawCoverEnd = rect.height / total; // when rect.top === viewportH - rect.height (bottom reaches viewport bottom)
    // If the element is shorter than the viewport, a true "cover" phase doesn't exist.
    // Clamp so coverStart..coverEnd is never inverted/collapsed into a tiny interval.
    const coverEnd = Math.max(rawCoverEnd, coverStart);
    const p = clamp01(percent / 100);
    if (keyword === 'entry')
        return clamp01(lerp(0, coverStart, p));
    if (keyword === 'cover')
        return clamp01(lerp(coverStart, coverEnd, p));
    return clamp01(lerp(coverEnd, 1, p)); // exit
}
function rangeProgress(overall, rect, viewportH, start, end) {
    const startP = timelineProgressFor(rect, viewportH, start[0], start[1]);
    const endP = timelineProgressFor(rect, viewportH, end[0], end[1]);
    return clamp01((overall - startP) / (endP - startP || 1));
}
class MapTrackAnimation {
    section;
    timelineEl;
    svgPath;
    images;
    svgRangeStart;
    svgRangeEnd;
    abortController;
    constructor(section) {
        this.section = section;
        this.timelineEl = section.querySelector('.map-track__container') ?? this.section;
        this.svgPath = section.querySelector('.map-track__path');
        this.images = [...section.querySelectorAll('.map-track__image')];
        this.abortController = new AbortController();
        // Read animation range config from data attributes (set by Liquid)
        this.svgRangeStart = parseRange(this.timelineEl.dataset.svgRangeStart ?? 'entry 40');
        this.svgRangeEnd = parseRange(this.timelineEl.dataset.svgRangeEnd ?? 'cover 80');
        // Set initial state
        if (this.svgPath) {
            // The Liquid section sets `pathLength="1"` on the <path>, so we can drive
            // dasharray/dashoffset in normalized 0–1 units (more reliable cross-browser).
            this.svgPath.style.strokeDasharray = '1';
            this.svgPath.style.strokeDashoffset = '1';
            this.svgPath.style.strokeOpacity = '0';
        }
        // Prep image initial state for JS-driven animation
        this.images.forEach((img, idx) => {
            const fromRight = (idx + 1) % 2 === 1;
            img.style.willChange = 'transform, opacity';
            img.style.opacity = '0';
            const dir = fromRight ? 1 : -1;
            img.style.transform = `translateX(${dir * (window.innerWidth || 1000)}px)`;
        });
        this.onScroll = this.onScroll.bind(this);
    }
    /**
     * Compute how far the section has scrolled through the viewport.
     * Returns 0 when section top enters viewport bottom,
     * returns 1 when section bottom exits viewport top.
     */
    getProgress(rect, viewportH) {
        // Total scroll distance = section height + viewport height
        const totalDistance = rect.height + viewportH;
        // How far we've scrolled into it
        const scrolled = viewportH - rect.top;
        return clamp01(scrolled / totalDistance);
    }
    onScroll() {
        const rect = this.timelineEl.getBoundingClientRect();
        const viewportH = window.innerHeight;
        const overall = this.getProgress(rect, viewportH);
        // 1) Drive SVG stroke using the same keyframe shape as `sections/map-track.liquid`
        //    (dashoffset 1 → 0, and opacity 0 → 1).
        if (this.svgPath) {
            const progress = rangeProgress(overall, rect, viewportH, this.svgRangeStart, this.svgRangeEnd);
            const dash01 = piecewiseLerp(SVG_DASHOFFSET_KEYFRAMES, progress);
            const opacity = piecewiseLerp(SVG_OPACITY_KEYFRAMES, progress);
            this.svgPath.style.strokeDashoffset = `${dash01}`;
            this.svgPath.style.strokeOpacity = `${opacity}`;
        }
        // 2) Drive images (slide-in + fade) with staggered ranges from data attributes.
        const w = window.innerWidth || 1000;
        this.images.forEach((img, idx) => {
            const rangeStart = parseRange(img.dataset.rangeStart ?? 'entry 0');
            const rangeEnd = parseRange(img.dataset.rangeEnd ?? 'entry 30');
            const t = rangeProgress(overall, rect, viewportH, rangeStart, rangeEnd);
            const fromRight = (idx + 1) % 2 === 1;
            const dir = fromRight ? 1 : -1;
            const x = lerp(dir * w, 0, t);
            img.style.opacity = `${t}`;
            img.style.transform = `translateX(${x}px)`;
        });
    }
    start() {
        // Use passive scroll + rAF for performance
        let ticking = false;
        window.addEventListener('scroll', () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(() => {
                    this.onScroll();
                    ticking = false;
                });
            }
        }, { passive: true, signal: this.abortController.signal });
        // Initial call
        this.onScroll();
    }
    destroy() {
        this.abortController.abort();
    }
}
// Init: only activate for unsupported browsers
document.addEventListener('DOMContentLoaded', () => {
    if (SUPPORTS_SCROLL_TIMELINE)
        return;
    document.querySelectorAll('.map-track-section').forEach((section) => {
        // Mark as JS-driven so CSS can opt out if needed.
        section.classList.add('map-track--js-driven');
        new MapTrackAnimation(section).start();
    });
});
/**
 * Aligns the meta panel vertically centred on the last (3rd) image when the
 * section is in "header shifted" mode. `--meta-top` is set to the image's
 * vertical centre; the CSS pairs it with `translateY(-50%)` to centre the panel.
 * Desktop-only; mobile layout uses `position: static` and ignores `--meta-top`.
 */
function syncHeaderShiftPosition(container) {
    const isDesktop = window.matchMedia('(min-width: 1060px)').matches;
    if (!isDesktop) {
        container.style.removeProperty('--meta-top');
        return;
    }
    const targetImage = container.querySelector('.map-track__images-wrapper > :last-child');
    if (!targetImage)
        return;
    const containerRect = container.getBoundingClientRect();
    const imageRect = targetImage.getBoundingClientRect();
    const center = imageRect.top - containerRect.top + imageRect.height / 2;
    container.style.setProperty('--meta-top', `${center}px`);
}
function syncAllShiftedContainers(root = document) {
    root.querySelectorAll('.map-track__container--header-shifted').forEach((container) => {
        syncHeaderShiftPosition(container);
        // Recompute when any image finishes loading — the last image's top offset
        // depends on the rendered heights of all the images stacked above it.
        container.querySelectorAll('.map-track__images-wrapper img').forEach((img) => {
            if (!img.complete) {
                img.addEventListener('load', () => syncHeaderShiftPosition(container), { once: true });
            }
        });
    });
}
function initHeaderShift() {
    syncAllShiftedContainers();
    // Debounced recompute on resize.
    let resizeTimer;
    window.addEventListener('resize', () => {
        if (resizeTimer !== undefined)
            window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => syncAllShiftedContainers(), 100);
    }, { passive: true });
    // Shopify theme editor: re-run when a section is re-rendered (toggle / settings change).
    document.addEventListener('shopify:section:load', (event) => {
        const target = event.target;
        if (target)
            syncAllShiftedContainers(target);
    });
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderShift);
}
else {
    initHeaderShift();
}
